import styled from "styled-components";
import { Button } from "@mui/material";
import { palette, typography } from "@src/core/styles";

type NpsContainerProps = {
  position: "bottom-right" | "bottom-left" | "top-right" | "top-left";
};

const getPositionStyles = (position: NpsContainerProps["position"]) => {
  switch (position) {
    case "bottom-right":
      return {
        bottom: "24px",
        right: "24px",
      };
    case "bottom-left":
      return {
        bottom: "24px",
        left: "24px",
      };
    case "top-right":
      return {
        top: "24px",
        right: "24px",
      };
    case "top-left":
      return {
        top: "24px",
        left: "24px",
      };
    default:
      return {
        bottom: "24px",
        right: "24px",
      };
  }
};

export const NpsContainer = styled.div<NpsContainerProps>`
  position: fixed;
  ${(props) => {
    const styles = getPositionStyles(props.position);
    return `
      ${props.position.includes("bottom") ? `bottom: ${styles.bottom};` : `top: ${styles.top};`}
      ${props.position.includes("right") ? `right: ${styles.right};` : `left: ${styles.left};`}
    `;
  }}
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

export const NpsTrigger = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${palette.colors.green_base};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const NpsForm = styled.form`
  background-color: ${palette.colors.surface};
  border-radius: 12px;
  padding: 24px;
  padding-top: 16px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 400px;
  animation: slideIn 0.3s ease-out;
  position: relative;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const FormHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 32px;
  line-height: 1;
  color: ${palette.colors.on_surface_variant};
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${palette.colors.outline_variant};
    color: ${palette.colors.on_surface};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const StarsContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

type StarButtonProps = {
  selected: boolean;
};

export const StarButton = styled.button<StarButtonProps>`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 32px;
  padding: 4px;
  transition: all 0.2s ease-in-out;
  filter: ${(props) => (props.selected ? "none" : "grayscale(100%) opacity(0.4)")};
  transform: ${(props) => (props.selected ? "scale(1.2)" : "scale(1)")};

  &:hover {
    transform: scale(1.3);
    filter: none;
  }

  &:active {
    transform: scale(1.1);
  }
`;

export const SubmitButton = styled(Button)`
  && {
    ${typography.body_large_medium};
    background-color: ${palette.colors.green_base};
    color: ${palette.colors.green10};
    padding: 12px 24px;
    border-radius: 8px;
    text-transform: none;
    margin-top: 8px;

    &:hover {
      background-color: ${palette.colors.button_hover};
    }

    &:disabled {
      background-color: ${palette.colors.outline_variant};
      color: ${palette.colors.on_surface_variant};
    }
  }
`;

export const ThankYouMessage = styled.div`
  ${typography.body_large_medium};
  color: ${palette.colors.green_base};
  text-align: center;
  padding: 24px;
`;

